import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { FormModal } from "../layout/Modal/Modal";
import { DeleteModal } from "../layout/deleteModal";
import { CgDanger } from "react-icons/cg";
import { FieldType } from "../../interfaces/FieldType";
import { ParcelCycle } from "../../interfaces/ParcelCycle";
import { ParcelData } from "../../interfaces/ParcelData";
import ParcelService from "../../services/parcelService";
import { formatDateInput, parseDateInput } from "../../utils/date";
import { SnackbarProps } from "../common/SnackbarProps";
import { Snackbar } from "../common/Snackbar";

interface CycleInfoPanelProps {
    currentParcel: ParcelData;
    currentCycle: ParcelCycle | null;
    refreshCycles?: (parcelId: number) => Promise<void>;
    showCycleButton?: boolean;
    toggleModal?: () => void;
}

export const CycleInfoPanel = ({
    currentParcel,
    currentCycle,
    refreshCycles,
    showCycleButton = false,
    toggleModal,
}: CycleInfoPanelProps) => {

    const [localCycle, setLocalCycle] = useState<ParcelCycle | null>(currentCycle);
    const [showDeleteModal, setDeleteModal] = useState(false);
    const [snackbar, setSnackbar] = useState<SnackbarProps>({ active: false });
    const [deleteConfirmed, setDeleteConfirmed] = useState<
        ((confirmed: boolean) => void) | null
    >(null);

    const parcelService = new ParcelService();


    useEffect(() => {
        setLocalCycle(currentCycle);
    }, [currentCycle]);


    const showDeleteConfirmation = (): Promise<boolean> => {
        return new Promise((resolve) => {
            setDeleteModal(true);
            const handleUserChoice = (confirmed: boolean) => {
                setDeleteModal(false);
                resolve(confirmed);
            };
            setDeleteConfirmed(() => handleUserChoice);
        });
    };

    const triggerSnackbar = (
        message: string,
        type: "success" | "error" | "info" | "warning"
    ) => {
        setSnackbar({ message, type, active: true });
        setTimeout(() => setSnackbar({ active: false }), 3000);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        console.log("change")
        const { name, value } = e.target;
        if (e.target.type === "date") {
            setLocalCycle((prev) => (prev ? { ...prev, [name]: parseDateInput(value) } : null));
        }
    };

    const formatInputDate = (dateString: string | Date | undefined) => {
        if (!dateString) return "";
        const date = dateString instanceof Date ? dateString : new Date(dateString);
        return formatDateInput(date as Date);
    };

    const closeCycle = async (e: FormEvent) => {
        e.preventDefault();
        if (!currentCycle || !currentParcel?.id) return;

        const isConfirmed = await showDeleteConfirmation();
        if (!isConfirmed) return;

        try {
            await parcelService.editCycleById(currentParcel.id, localCycle!.id!, {
                ...localCycle!,
                completed: true,
            });


            const newCycle = await parcelService.createCycle(currentParcel.id, {
                startDate: new Date(),
                completed: false,
            });
            if (refreshCycles) {
                await refreshCycles(currentParcel.id);
            }
            setLocalCycle(newCycle);
            triggerSnackbar("Zyklus abgeschlossen und neuer Zyklus wurde erstellt ✅", "success");
        } catch (error) {
            triggerSnackbar("Zyklus konnte nicht abgeschlossen werden ❌", "error");
        }
    };

    return (
        <div className="flex-1 border border-gray-300 p-3 min-h-[200px] rounded-lg bg-white shadow">
            <h3 className="font-semibold mb-2">Informationen</h3>

            <div>
                <p>
                    <b>Parzelle:</b> {currentParcel?.name}
                </p>
                <p>
                    <b>Schlagkarte:</b>{" "}
                    {currentParcel?.type === FieldType.Ackerbau
                        ? "Ackerbau"
                        : "Futterbau"}
                </p>
                <p>
                    <b>Subtype:</b> {currentParcel?.subtype || "-"}
                </p>
                <p>
                    <b>Status:</b>{" "}
                    {localCycle!.completed ? "Abgeschlossen 🔴" : "Aktiv 🟢"}
                </p>

                {showCycleButton && (
                    <button
                        className="mt-2 bg-green-800 text-white px-4 py-2 rounded-md hover:bg-green-700"
                        onClick={toggleModal}
                    >
                        Zyklus anzeigen
                    </button>
                )}

            </div>

            <form className="py-6 space-y-3" onSubmit={closeCycle}>
                <div className="space-y-1">
                    <label className="block font-medium" htmlFor="startDate">
                        Start:
                    </label>
                    <input
                        id="startDate"
                        name="startDate"
                        type="date"
                        value={formatInputDate(localCycle!.startDate)}
                        onChange={handleChange}
                        className="border border-gray-300 rounded-md p-2"
                    />
                </div>
                <div className="space-y-1">
                    <label className="block font-medium" htmlFor="endDate">
                        Ende:
                    </label>
                    <input
                        id="endDate"
                        name="endDate"
                        type="date"
                        value={formatInputDate(localCycle!.endDate)}
                        onChange={handleChange}
                        className="border border-gray-300 rounded-md p-2"
                        required
                    />
                </div>
                <button
                    type="submit"
                    className={`w-[90%] py-2 rounded-md text-white font-semibold ${localCycle!.completed
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-800 hover:bg-green-700"
                        }`}
                    disabled={localCycle!.completed}
                >
                    Zyklus abschliessen
                </button>
            </form>

            <FormModal open={showDeleteModal}>
                <DeleteModal onSubmit={deleteConfirmed!}>
                    <p
                        style={{
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                        }}
                    >
                        <b>Zyklus</b> wirklich endgültig abschliessen? <CgDanger size={22} />
                    </p>
                </DeleteModal>
            </FormModal>

            {snackbar.active && (
                <Snackbar message={snackbar.message!} type={snackbar.type} />
            )}
        </div>
    );
};
